if (!customElements.get("wdn-dropdown")) {
  class WDNDropdown extends HTMLElement {
    constructor() {
      super();
      this.buildConstructor();
      if (!this.autoInit) return;
      this.init();
    }

    configureAttributes() {
      this.nameSpace = this.getAttribute("data-name") || "dropdown";

      this.selectors = {
        trigger: "[" + this.nameSpace + "-trigger]",
        container: "[" + this.nameSpace + "-container]",
        content: "[" + this.nameSpace + "-content]",
      };

      this.attributeList = {
        group: "group",
        active: this.nameSpace + "-active",
        resetHeight: "data-reset-height",
        animation: this.nameSpace + "-animate",
        active_on: "active-on",
      };
    }

    buildConstructor() {
      this.configureAttributes();
      this.triggers = this.querySelectorAll(this.selectors.trigger);
      this.container = this.querySelector(this.selectors.container);
      this.content = this.querySelector(this.selectors.content);
      this.group = this.getAttribute(this.attributeList.group);
      this.isActive = this.getAttribute(this.attributeList.active) === "";
      this.resetHeight =
        this.getAttribute(this.attributeList.resetHeight) === "";
      this.animation =
        parseInt(this.getAttribute(this.attributeList.animation)) || 0;
      this.autoInit = !(this.getAttribute("data-autoinit") === "false");
      this.restrictions = {
        is_restricted: this.hasAttribute(this.attributeList.active_on),
        active_on: this.getAttribute(this.attributeList.active_on),
        valid_list: ["mobile", "desktop"],
        // The CSS's breakpoint (component-dropdown.css): at 900, 900–979px showed a closed footer it would not open.
        vp_width: 980,
      };
      this.setConditionals();
    }

    setConditionals() {
      // Used to determine if clicks should be disabled based on these conditionals.
      this.conditionals = [
        window.innerWidth >= this.restrictions.vp_width &&
          this.restrictions.active_on === this.restrictions.valid_list[0],
        window.innerWidth < this.restrictions.vp_width &&
          this.restrictions.active_on === this.restrictions.valid_list[1],
      ];
    }

    setState(newState) {
      this.isActive = newState;
    }

    setContainerHeight(newHeight) {
      var value = newHeight;
      if (typeof newHeight === "number") {
        value = newHeight + "px";
      }
      this.container.style.height = value;
    }

    shrinkContainer() {
      this.setContainerHeight(this.initialHeight);
    }

    expandContainer() {
      var contentHeight = this.content.offsetHeight;
      this.setContainerHeight(contentHeight);
    }

    setInactive() {
      this.expandContainer();
      setTimeout(
        function () {
          this.setState(false);
          this.removeAttribute(this.attributeList.active);
          this.shrinkContainer();

          // this will help when extending the component
          if (typeof this.onClose === "function") {
            this.onClose();
          }
        }.bind(this),
        50
      );
    }

    scrollTop() {
      if (!this.classList.contains("scroll-top--on-active")) return;

      let offsetTop =
        100 +
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--nav-height"
          )
        );

      setTimeout(() => {
        window.scrollTo({
          behaviour: "smooth",
          top: this.getBoundingClientRect().top + window.scrollY - offsetTop,
        });
      }, 350);
    }

    setActive() {
      this.collapseGroupContainers();
      this.setState(true);
      this.setAttribute(this.attributeList.active, "");
      this.expandContainer();

      this.scrollTop();
      // this will help when extending the component
      if (typeof this.onOpen === "function") {
        this.onOpen();
      }
    }

    fireGroupEvent() {
      var customEvent = new CustomEvent("dropdown:group:collapse", {
        detail: { initiator: this, group: this.group },
      });
      document.body.dispatchEvent(customEvent);
    }

    collapseGroupContainers() {
      if (this.group) {
        this.fireGroupEvent();
      }
    }

    beforeInactive() {
      this.expandContainer();
    }

    afterActive() {
      if (!this.animation) return;
      setTimeout(
        function () {
          this.setContainerHeight("auto");
        }.bind(this),
        this.animation
      );
    }

    validateActiveOn() {
      var valid = true;
      if (!this.restrictions.is_restricted) return valid;
      if (
        this.restrictions.valid_list.indexOf(this.restrictions.active_on) === -1
      )
        return valid;
      if (this.conditionals.some((condition) => condition === true))
        valid = false;

      return valid;
    }

    onTriggerClick() {
      // Check if restricted to only be used phone/desktop. Looks at active-on attribute and if it has been set to mobile or desktop, then break.
      if (!this.validateActiveOn()) return;

      if (this.isActive) {
        this.beforeInactive();
        this.setInactive();
      } else {
        this.setActive();
        this.afterActive();
      }

      if (typeof this.onClick === "function") {
        this.onClick();
      }
    }

    // A closed fold-out is out of sight (height 0) but its content is still in the page: inert keeps its links and
    // buttons out of the Tab order and away from a screen reader, as "Videoer" did (2026-11-02). It follows the
    // active attribute itself, so code that sets that directly (storefront-filtering.js, a server-rendered open one)
    // is covered too. Left alone where the content shows anyway: active-on="mobile" from 980px (CSS), or a closed
    // height above 0 (a peek).
    // Inert content needs a way in for the keyboard: a fold-out that takes focus itself (tabindex, the product page's)
    // opens on Enter or Space (bind); any other gets its heading as a button (role, tabindex, aria-expanded), only
    // where it works as one (not active-on="mobile" on a desktop).
    syncInert() {
      if (!this.container) return;
      var open = this.hasAttribute(this.attributeList.active);
      var alwaysShown = this.restrictions.active_on === "mobile" && window.matchMedia("(min-width: 980px)").matches;
      var shown = open || this.initialHeight > 0 || alwaysShown;
      if (this.container.inert === shown) this.container.inert = !shown;

      var trigger = this.keyTrigger;
      if (!trigger) return;
      if (this.validateActiveOn() && !alwaysShown) {
        trigger.setAttribute("role", "button");
        trigger.setAttribute("tabindex", "0");
        trigger.setAttribute("aria-expanded", open ? "true" : "false");
      } else {
        ["role", "tabindex", "aria-expanded"].forEach((name) => trigger.removeAttribute(name));
      }
    }

    // Enter or Space on the fold-out (when it takes focus) or on its heading: as a click on the heading. Keys in its
    // content (a link, a form) and in a fold-out inside it are theirs.
    onKeydown(e) {
      if ((e.key !== "Enter" && e.key !== " ") || e.defaultPrevented) return;
      var target = e.target;
      var own = target === this || (target.matches(this.selectors.trigger) && target.closest("wdn-dropdown") === this);
      if (!own) return;
      e.preventDefault();
      this.onTriggerClick();
    }

    onWindowResize() {
      this.setConditionals();
      this.syncInert();
      if (this.isActive) {
        this.expandContainer();
      }
    }

    onGroupCollapse(e) {
      if (e.detail.initiator === this) return;
      if (this.group === e.detail.group && this.isActive) {
        this.setInactive();
      }
    }

    setInitialState() {
      if (this.isActive) {
        this.expandContainer();
      }
    }

    storeInitialHeight() {
      this.initialHeight = parseInt(this.container.style.height) || 0;
    }

    bind() {
      this.triggers.forEach((trigger) =>
        trigger.addEventListener("click", this.onTriggerClick.bind(this))
      );
      this.addEventListener("keydown", this.onKeydown.bind(this));
      // The heading that becomes a button (syncInert): the first that is this fold-out's own and not hidden from a
      // screen reader (Videoer's peek); none when the fold-out takes focus itself.
      if (!this.hasAttribute("tabindex")) {
        this.keyTrigger = Array.prototype.find.call(this.triggers, (t) => t.closest("wdn-dropdown") === this && !t.closest("[aria-hidden='true']"));
      }
      this.onGroupCollapseBound = this.onGroupCollapse.bind(this);
      this.onWindowResizeBound = this.onWindowResize.bind(this);
      this.bindOutside();
    }

    // The page-wide listeners, dropped while the dropdown is out of the page (a fold-out a variant change replaces)
    // and taken up again if it comes back.
    bindOutside() {
      if (this.outsideBound) return;
      document.body.addEventListener("dropdown:group:collapse", this.onGroupCollapseBound);
      window.addEventListener("resize", this.onWindowResizeBound);
      this.outsideBound = true;
    }

    connectedCallback() {
      if (this.onGroupCollapseBound) this.bindOutside();
    }

    disconnectedCallback() {
      if (!this.outsideBound) return;
      document.body.removeEventListener("dropdown:group:collapse", this.onGroupCollapseBound);
      window.removeEventListener("resize", this.onWindowResizeBound);
      this.outsideBound = false;
    }

    init() {
      this.storeInitialHeight();
      this.setInitialState();
      this.bind();
      this.syncInert();
      new MutationObserver(this.syncInert.bind(this)).observe(this, {
        attributes: true,
        attributeFilter: [this.attributeList.active],
      });
    }
  }
  customElements.define("wdn-dropdown", WDNDropdown);
}
